import { poline } from './palette';
import { hslToHex, RubiksCube } from './cube.ts';
import { initLiquidGlass } from './liquid-glass.ts';
import './cube.ts'
import './style.css'

const paletteStrip = document.getElementById('palette-strip');
poline.colorsCSS.forEach(color => {
  const element = document.createElement('li');
  element.style.background = color;
  paletteStrip?.appendChild(element);
})

const zero = poline.colors[0]
const root = document.documentElement;
root.style.setProperty('--bg', `hsl(${zero[0]}, ${zero[1] * 100}%, ${Math.min(zero[2] * 100 + 95 * (1 - zero[2]), 100)}%)`);
root.style.setProperty('--primary', poline.colorsCSS[1]);
const accent = poline.colors[1] as HSL;
root.style.setProperty(
  '--accent',
  `hsl(${accent[0]}, ${accent[1] * 100}%, ${Math.min(accent[2] * 100 + 70 * (1 - accent[2]), 100)}%)`,
);
root.style.setProperty('--secondary', poline.colorsCSS[4]);
root.style.setProperty('--text', poline.colorsCSS[2]);
const muted = poline.colors[2] as HSL;
root.style.setProperty(
  '--muted',
  `hsl(${muted[0]}, ${muted[1] * 100}%, ${Math.min(muted[2] * 100 + 30 * (1 - muted[2]), 100)}%)`,
);

type HSL3 = [number, number, number];

const faceVars = poline.colors.slice(0, 6).map(([h, s, l]) => ({
  primary: `hsl(${h}, ${s * 100}%, ${l * 100}%)`,
  accent: `hsl(${h}, ${s * 100}%, ${Math.min(l * 100 + 70 * (1 - l), 100)}%)`,
  muted: `hsl(${h}, ${s * 100}%, ${Math.min(l * 100 + 30 * (1 - l), 100)}%)`,
  text: `hsl(${h}, ${s * 100}%, ${Math.max(l * 100 * 0.35, 8)}%)`,
  secondary: `hsl(${(h + 180) % 360}, ${s * 100}%, ${l * 100}%)`,
  bg: `hsl(${h}, ${s * 100}%, ${Math.min(l * 100 + 95 * (1 - l), 100)}%)`,
}));

const faceCanvasHSL = poline.colors.slice(0, 6).map(([h, s, l]) => ({
  accent: [h, s * 100, Math.min(l * 100 + 70 * (1 - l), 100)] as HSL3,
  bg: [h, s * 100, Math.min(l * 100 + 95 * (1 - l), 100)] as HSL3,
}));

const origCanvasHSL = {
  accent: (() => { const c = poline.colors[1]; return [c[0], c[1] * 100, Math.min(c[2] * 100 + 70 * (1 - c[2]), 100)] as HSL3; })(),
  bg: (() => { const c = poline.colors[0]; return [c[0], c[1] * 100, Math.min(c[2] * 100 + 95 * (1 - c[2]), 100)] as HSL3; })(),
};

for (let i = 0; i < 6; i++) {
  const wrap = document.getElementById(`label-${i}-wrap`);
  if (wrap) {
    wrap.style.setProperty('--primary', faceVars[i].primary);
    wrap.style.setProperty('--accent', faceVars[i].accent);
    wrap.style.setProperty('--muted', faceVars[i].muted);
    wrap.style.setProperty('--secondary', faceVars[i].secondary);
    wrap.style.setProperty('--text', faceVars[i].text);
    wrap.style.setProperty('--bg', faceVars[i].bg);
  }
}

const originalVars = {
  primary: root.style.getPropertyValue('--primary'),
  accent: root.style.getPropertyValue('--accent'),
  muted: root.style.getPropertyValue('--muted'),
  secondary: root.style.getPropertyValue('--secondary'),
  text: root.style.getPropertyValue('--text'),
  bg: root.style.getPropertyValue('--bg'),
};

type RGB = [number, number, number];
type HSL = [number, number, number];

function hslToRgb([h, s, l]: HSL): RGB {
  const k = (n: number): number => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number =>
    l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return [f(0), f(8), f(4)];
}

function setDuotone(filterId: string, darkColor: HSL, lightColor: HSL): void {
  const [dr, dg, db] = hslToRgb(darkColor);
  const [lr, lg, lb] = hslToRgb(lightColor);
  const NS = 'http://www.w3.org/2000/svg';

  const filter = document.getElementById(filterId);
  if (!filter) return;
  filter.innerHTML = '';

  const matrix = document.createElementNS(NS, 'feColorMatrix');
  matrix.setAttribute('type', 'matrix');
  matrix.setAttribute(
    'values',
    '0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0.33 0.33 0.33 0 0  0 0 0 1 0',
  );
  filter.appendChild(matrix);

  const transfer = document.createElementNS(NS, 'feComponentTransfer');
  transfer.setAttribute('color-interpolation-filters', 'sRGB');

  const channels: ReadonlyArray<[string, number, number]> = [
    ['feFuncR', dr, lr],
    ['feFuncG', dg, lg],
    ['feFuncB', db, lb],
  ];
  for (const [tag, d, l] of channels) {
    const f = document.createElementNS(NS, tag);
    f.setAttribute('type', 'table');
    f.setAttribute('tableValues', `${d} ${l}`);
    transfer.appendChild(f);
  }
  filter.appendChild(transfer);
}

function darken([h, s, l]: HSL, amount = 0.3): HSL {
  return [h, s, Math.max(0, l - amount)];
}

setDuotone('duotone', darken(poline.colors[0] as HSL, 0.5), poline.colors[6] as HSL);
setDuotone('tint', darken(poline.colors[0] as HSL, 0.5), [0, 0, 1]);

const labels = Array.from({ length: 6 }, (_, i) => document.getElementById('label-' + i + '-wrap'));
const faceColors = poline.colors.slice(0, 6).map(([h, s, l]) => hslToHex(h, s, l));

const rootStyle = getComputedStyle(document.documentElement);
const originalAsciiBg = `color-mix(in srgb, ${rootStyle.getPropertyValue('--accent').trim() || 'transparent'}, white 50%)`;
const originalCellBg = rootStyle.getPropertyValue('--bg').trim() || '#ffffff';
const cube = new RubiksCube({
  asciiCanvas: document.getElementById('ascii-canvas') as HTMLCanvasElement,
  faceColors,
  labelElements: labels,
  asciiBg: originalAsciiBg,
  cellBg: originalCellBg,
  palette: poline.colors.map(([h, s, l]) => hslToHex(h, s, l)),
  asciiResolution: 0.05,
});

document.getElementById('scramble-btn')?.addEventListener('click', () => cube.scramble());
document.getElementById('reset-btn')?.addEventListener('click', () => cube.solve());

cube.start();

let activeSection: number | null = null;

let curCanvasHSL = { accent: [...origCanvasHSL.accent] as HSL3, bg: [...origCanvasHSL.bg] as HSL3 };
let canvasAnimId: number | null = null;

function lerpHue(a: number, b: number, t: number) {
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return ((a + d * t) % 360 + 360) % 360;
}

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function lerpHSL(from: HSL3, to: HSL3, t: number): HSL3 {
  return [lerpHue(from[0], to[0], t), from[1] + (to[1] - from[1]) * t, from[2] + (to[2] - from[2]) * t];
}

function transitionCanvas(target: { accent: HSL3; bg: HSL3 }) {
  const from = { accent: [...curCanvasHSL.accent] as HSL3, bg: [...curCanvasHSL.bg] as HSL3 };
  if (canvasAnimId !== null) cancelAnimationFrame(canvasAnimId);
  const t0 = performance.now();

  function tick() {
    const raw = Math.min((performance.now() - t0) / 200, 1);
    const t = easeInOut(raw);
    curCanvasHSL.accent = lerpHSL(from.accent, target.accent, t);
    curCanvasHSL.bg = lerpHSL(from.bg, target.bg, t);
    const [ah, as, al] = curCanvasHSL.accent;
    const [bh, bs, bl] = curCanvasHSL.bg;
    cube.setAsciiBg(`color-mix(in srgb, hsl(${ah}, ${as}%, ${al}%), white 50%)`);
    cube.setCellBg(`hsl(${bh}, ${bs}%, ${bl}%)`);
    if (raw < 1) canvasAnimId = requestAnimationFrame(tick);
    else canvasAnimId = null;
  }

  canvasAnimId = requestAnimationFrame(tick);
}

function setupExpandingLabel(index: number) {
  const wrap = document.getElementById(`label-${index}-wrap`);
  const btn = document.getElementById(`label-${index}`);
  const card = document.getElementById(`label-${index}-card`);
  const blur = document.getElementById(`label-${index}-blur`);
  const inner = document.getElementById(`label-${index}-inner`);
  const close = document.getElementById(`label-${index}-close`);

  if (!wrap || !btn || !card || !blur || !inner || !close) return;

  // Move close button to card so it's not affected by inner's mask-image
  card.appendChild(close);
  close.style.opacity = '0';

  // Scroll indicators
  const topArrow = document.createElement('div');
  topArrow.className = 'scroll-arrow scroll-arrow-top';
  topArrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z" clip-rule="evenodd" /></svg>';
  const bottomArrow = document.createElement('div');
  bottomArrow.className = 'scroll-arrow scroll-arrow-bottom';
  bottomArrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" /></svg>';
  card.appendChild(topArrow);
  card.appendChild(bottomArrow);

  function updateScrollIndicators() {
    const { scrollTop, scrollHeight, clientHeight } = inner!;
    const isScrollable = scrollHeight > clientHeight + 2;

    if (!isScrollable) {
      topArrow.style.opacity = '0';
      bottomArrow.style.opacity = '0';
      inner!.style.maskImage = '';
      return;
    }

    const atTop = scrollTop <= 2;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 2;

    topArrow.style.opacity = atTop ? '0' : '1';
    bottomArrow.style.opacity = atBottom ? '0' : '1';

    if (!atTop && !atBottom) {
      inner!.style.maskImage = 'linear-gradient(to bottom, transparent, black 40px, black calc(100% - 40px), transparent)';
    } else if (!atTop) {
      inner!.style.maskImage = 'linear-gradient(to bottom, transparent, black 40px)';
    } else if (!atBottom) {
      inner!.style.maskImage = 'linear-gradient(to bottom, black calc(100% - 40px), transparent)';
    } else {
      inner!.style.maskImage = '';
    }
  }

  inner.addEventListener('scroll', updateScrollIndicators, { passive: true });

  let open = false;
  let t: number | undefined;
  let targetW = 0, targetH = 0;

  function measure() {
    const prevW = card!.style.width, prevH = card!.style.height, prevO = card!.style.opacity;
    card!.style.opacity = '0';
    card!.style.width = 'max-content';
    card!.style.height = 'auto';
    targetW = card!.offsetWidth;
    targetH = card!.offsetHeight;
    card!.style.width = prevW;
    card!.style.height = prevH;
    card!.style.opacity = prevO;
  }

  function expand() {
    if (open) return;
    open = true;
    window.clearTimeout(t);
    if (!targetW) measure();

    if (index < 6) {
    wrap!.style.zIndex = '40';
    } else if (index === 6) {
      wrap!.style.zIndex = '50';
    }

    btn!.style.opacity = '0';
    btn!.style.pointerEvents = 'none';

    card!.style.width = `${targetW}px`;
    card!.style.height = `${targetH}px`;
    card!.style.opacity = '1';
    card!.style.pointerEvents = 'auto';

    blur!.style.width = `${targetW + 20}px`;
    blur!.style.height = `${targetH + 20}px`;
    blur!.style.opacity = '1';

    wrap!.dataset.locked = 'true';
    
    if (index == 6) {
      wrap!.style.top = '50%';
      wrap!.style.right = '50%';
      wrap!.style.transform = 'translate(50%, -50%)';
    }

    if (index < 6) {
      activeSection = index;
      root.style.setProperty('--primary', faceVars[index].primary);
      root.style.setProperty('--accent', faceVars[index].accent);
      root.style.setProperty('--muted', faceVars[index].muted);
      root.style.setProperty('--secondary', faceVars[index].secondary);
      root.style.setProperty('--text', faceVars[index].text);
      root.style.setProperty('--bg', faceVars[index].bg);
      transitionCanvas(faceCanvasHSL[index]);
    }

    t = window.setTimeout(() => {
      inner!.style.opacity = '1';
      close!.style.opacity = '';
      updateScrollIndicators();
    }, 460);
  }

  function collapse() {
    if (!open) return;
    open = false;
    window.clearTimeout(t);

    inner!.style.opacity = '0';
    close!.style.opacity = '0';
    inner!.style.maskImage = '';
    topArrow.style.opacity = '0';
    bottomArrow.style.opacity = '0';

    if (activeSection === index) {
      activeSection = null;
      root.style.setProperty('--primary', originalVars.primary);
      root.style.setProperty('--accent', originalVars.accent);
      root.style.setProperty('--muted', originalVars.muted);
      root.style.setProperty('--secondary', originalVars.secondary);
      root.style.setProperty('--text', originalVars.text);
      root.style.setProperty('--bg', originalVars.bg);
      transitionCanvas(origCanvasHSL);
    }

    t = window.setTimeout(() => {
      card!.style.width = ''; card!.style.height = '';
      card!.style.opacity = '0'; card!.style.pointerEvents = 'none';
      blur!.style.width = ''; blur!.style.height = '';
      inner!.scrollTop = 0;
      if (index == 6) {
        wrap!.style.top = '';
        wrap!.style.right = '';
        wrap!.style.transform = '';
      }
      window.setTimeout(() => {
        btn!.style.opacity = ''; btn!.style.pointerEvents = '';
        wrap!.style.zIndex = '';
        delete wrap!.dataset.locked;
      }, 320);
    }, 200);
  }

  window.addEventListener('resize', () => {
    targetW = 0;
    targetH = 0;
    if (open) {
      measure();
      card!.style.width = `${targetW}px`;
      card!.style.height = `${targetH}px`;
      blur!.style.width = `${targetW + 20}px`;
      blur!.style.height = `${targetH + 20}px`;
      updateScrollIndicators();
    }
  });

  btn.addEventListener('click', expand);
  close.addEventListener('click', (e) => { e.stopPropagation(); collapse(); });
  document.addEventListener('click', (e) => {
    if (open && !wrap.contains(e.target as Node)) collapse();
  });
  wrap.addEventListener('label:offscreen', () => collapse());
}

// Call for whichever labels need it:
[0, 2, 3, 4, 6].forEach(i => setupExpandingLabel(i));
document.getElementById('label-1')?.addEventListener('click', () => cube.scramble());
document.getElementById('label-5')?.addEventListener('click', () => cube.solve());

const bgValue = root.style.getPropertyValue('--bg');
let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
if (!themeMeta) {
  themeMeta = document.createElement('meta');
  themeMeta.name = 'theme-color';
  document.head.appendChild(themeMeta);
}
themeMeta.content = bgValue;


// Block pinch-zoom on iOS Safari
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
document.addEventListener('gestureend', (e) => e.preventDefault());

// Block double-tap zoom
let lastTouchEnd = 0;
document.addEventListener('touchend', (e) => {
  const now = Date.now();
  if (now - lastTouchEnd <= 300) {
    e.preventDefault();
  }
  lastTouchEnd = now;
}, { passive: false });

function runCubeTutorial() {
  const hand = document.getElementById('cube-tutorial-hand');
  const open = document.getElementById('hand-open');
  const grab = document.getElementById('hand-grab');
  if (!hand || !open || !grab) return;

  let cancelled = false;
  let raf: number | undefined;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    if (raf) cancelAnimationFrame(raf);
    hand.style.opacity = '0';
    hand.style.filter = 'blur(4px)';
    setTimeout(() => hand.remove(), 300);
    cleanup();
  };

  // Cancel on any user interaction with the cube
  const canvas = document.getElementById('ascii-canvas');
  const onInteract = () => cancel();
  canvas?.addEventListener('pointerdown', onInteract);
  window.addEventListener('keydown', onInteract);

  function cleanup() {
    canvas?.removeEventListener('pointerdown', onInteract);
    window.removeEventListener('keydown', onInteract);
  }

  // Phase timings (ms)
  const APPROACH = 800;   // hand fades in and slides toward center
  const GRAB_PAUSE = 300; // pause to "grab"
  const DRAG = 1500;      // dragging across, rotating cube
  const RELEASE = 400;    // release and fade out

  const startX = window.innerWidth * 0.15;
  const endX = window.innerWidth * 0.85;
  const y = window.innerHeight * 0.5;

  // Total cube rotation across the drag (radians)
  const totalYaw = -Math.PI * 0.6;  // ~108°, feels like a satisfying spin

  let lastYaw = 0;
  const t0 = performance.now();

  function tick(now: number) {
    if (cancelled || !hand || !grab || !open) return;
    const elapsed = now - t0;

    if (elapsed < APPROACH) {
      const t = elapsed / APPROACH;
      const eased = 1 - Math.pow(1 - t, 3);
      hand.style.opacity = String(eased);
      hand.style.filter = `blur(${(1 - eased) * 64}px)`;
      hand.style.left = `${startX * eased}px`;
      hand.style.top = `${y}px`;
    } else if (elapsed < APPROACH + GRAB_PAUSE) {
      hand.style.opacity = '1';
      hand.style.left = `${startX}px`;
      open.classList.add('hidden');
      grab.classList.remove('hidden');
    } else if (elapsed < APPROACH + GRAB_PAUSE + DRAG) {
      const t = (elapsed - APPROACH - GRAB_PAUSE) / DRAG;
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const x = startX + (endX - startX) * eased;
      hand.style.left = `${x}px`;

      const targetYaw = totalYaw * eased;
      const deltaYaw = targetYaw - lastYaw;
      cube.rotateView(deltaYaw);
      lastYaw = targetYaw;
    } else if (elapsed < APPROACH + GRAB_PAUSE + DRAG + RELEASE) {
      // Release: snap cube back to original, fade hand out
      const t = (elapsed - APPROACH - GRAB_PAUSE - DRAG) / RELEASE;
      const eased = 1 - Math.pow(1 - t, 3);  // ease-out cubic, snappy

      open.classList.remove('hidden');
      grab.classList.add('hidden');
      hand.style.opacity = String(1 - t);

      // Rotate back from totalYaw → 0
      const targetYaw = totalYaw * (1 - eased);
      const deltaYaw = targetYaw - lastYaw;
      cube.rotateView(deltaYaw);
      lastYaw = targetYaw;
    } else {
      // Make sure we end exactly at zero
      cube.rotateView(-lastYaw);
      lastYaw = 0;
      cancel();
      return;
    }

    raf = requestAnimationFrame(tick);
  }

  raf = requestAnimationFrame(tick);
}

initLiquidGlass();

// Run after cube starts, slight delay so the page settles
setTimeout(runCubeTutorial, 800);

